import { useCallback, useEffect, useState } from 'react';
import _get from 'lodash/get';
import {
  DepartmentHierarchyNodeFragmentFragment as DepartmentHierarchyNode,
  useDepartmentHierarchyChildrenLazyQuery,
} from 'graphql-common';
import isAbortError from '@lib/utils/isAbortError';
import { captureException } from '@lib/utils/captureException';
import useDepartmentAncestorChain from '../../utils/useDepartmentAncestorChain';

export default function useDepartmentHierarchyTree(departmentId: string) {
  const { ancestorChain, loading } = useDepartmentAncestorChain(departmentId);
  const [fetchChildren] = useDepartmentHierarchyChildrenLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [childrenByParentId, setChildrenByParentId] = useState<
    Record<string, DepartmentHierarchyNode[]>
  >({});
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const loadChildren = useCallback(
    async (parentId: string) => {
      setLoadingIds((prev) => new Set(prev).add(parentId));
      try {
        const { data } = await fetchChildren({ variables: { id: parentId } });
        const children = _get(
          data,
          'data.children',
          [],
        ) as DepartmentHierarchyNode[];
        setChildrenByParentId((prev) => ({ ...prev, [parentId]: children }));
      } catch (error) {
        // A superseding fetchChildren call for another node can abort this
        // one (shared lazy-query instance) - that's expected, only report
        // real failures. Either way, leave this node's children unset so
        // the toggle can be retried.
        if (!isAbortError(error)) captureException(error);
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
    },
    [fetchChildren],
  );

  // Auto-expand and preload every ancestor above the current department, so
  // the widget opens already showing the path down to it (BE contract: one
  // `children` fetch per level to unfold a node).
  useEffect(() => {
    const ancestorsAboveCurrent = ancestorChain.slice(0, -1);
    if (!ancestorsAboveCurrent.length) return;
    setExpandedIds(
      (prev) =>
        new Set([...prev, ...ancestorsAboveCurrent.map(({ id }) => id)]),
    );
    const idsToLoad = ancestorsAboveCurrent
      .map(({ id }) => id)
      .filter((id) => !childrenByParentId[id] && !loadingIds.has(id));
    if (!idsToLoad.length) return;
    (async () => {
      // Sequential on purpose: fetchChildren shares one lazy-query instance
      // (single ObservableQuery), so firing it for every level at once would
      // have each new call cancel the previous level's still-in-flight one.
      // eslint-disable-next-line no-restricted-syntax
      for (const id of idsToLoad) {
        // eslint-disable-next-line no-await-in-loop
        await loadChildren(id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ancestorChain]);

  const toggleExpand = (node: DepartmentHierarchyNode) => {
    const isExpanded = expandedIds.has(node.id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (isExpanded) {
        next.delete(node.id);
      } else {
        next.add(node.id);
      }
      return next;
    });
    if (
      !isExpanded &&
      !childrenByParentId[node.id] &&
      !loadingIds.has(node.id)
    ) {
      loadChildren(node.id);
    }
  };

  // Only the top ancestor of the current department is shown as a root, not
  // every root-level department in the company.
  const roots = ancestorChain.length ? [ancestorChain[0]] : [];

  return {
    roots,
    loading,
    childrenByParentId,
    expandedIds,
    loadingIds,
    toggleExpand,
  };
}
